import { useCallback, useRef } from "react";
import { Button, Group, Input, NativeSelect, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DatePickerInput } from "@mantine/dates";
import { Editor } from "@tiptap/react";
import { MarketingCampaignFields, NewRequestFormProps } from "../../types/request.types";
import ContentEditor from "../ContentEditor";
import FormAttachments from "../attachments/FormAttachments";
import FormContacts from "../contacts/FormContacts";


export default function MarketingCampaignForm(props: NewRequestFormProps) {
  const { onSubmit, tempId } = props;
  const editorRef = useRef<Editor>(null);
  const form = useForm<MarketingCampaignFields>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      project_type: null,
      budget: '',
      when_to_begin: null,
      description: '',
      requested_completion_date: null
    },
    validate: {
      description: val => (
        editorRef.current && editorRef.current.getText().length > 0 ?
          null : 'Please fill out this field'
      ),
      requested_completion_date: val => (
        val ? null : 'Please fill out this field'
      )
    }
  });

  const onFormSubmit = useCallback((submitVals: MarketingCampaignFields) => {
    const newRequest = { ...submitVals };
    newRequest.description = editorRef.current?.getHTML();
    onSubmit(newRequest);
  }, []);
  
  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Input.Wrapper
        label="Contacts"
        mt="md"
      >
        <FormContacts<MarketingCampaignFields> form={form} />
      </Input.Wrapper>
      
      <TextInput
        {...form.getInputProps('name')}
        key={form.key('name')}
        label="Project name"
        mt="md"
        required
      />
      
      <NativeSelect
        {...form.getInputProps('project_type')}
        key={form.key('project_type')}
        label="Type"
        mt="md"
        data={['', 'Redesign', 'New'].map(pt => ({
          value: pt.toLowerCase(), label: pt
        }))}
      />

      <TextInput
        {...form.getInputProps('budget')}
        key={form.key('budget')}
        label="Budget"
        mt="md"
      />

      <DatePickerInput
        {...form.getInputProps('when_to_begin')}
        key={form.key('when_to_begin')}
        label="When would you like this marketing initiative to begin?"
        highlightToday
        mt="md"
      />

      <ContentEditor<MarketingCampaignFields>
        formKey="description"
        label="Description"
        required={true}
        form={form} 
        editorRef={editorRef}
        mt="md"
      />

      <DatePickerInput
        {...form.getInputProps('requested_completion_date')}
        key={form.key('requested_completion_date')}
        label="Requested completion date"
        required
        highlightToday
        mt="md"
      />

      <Input.Wrapper
        label="Attachments"
        mt="md"
      >
        <FormAttachments tempId={tempId} />
      </Input.Wrapper>

      <Group justify="flex-end" mt="md" bg="gray.1" p="sm">
        <Button type="submit">Submit</Button>
      </Group>

    </form>
  );
}